import { msg } from "@lingui/core/macro";
import { createSignal, For, Show } from "solid-js";
import { ChevronRight, Plus, Users } from "lucide-solid";
import { Link } from "@/lib/u-router";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { TextField } from "@/components/ui/text-field";
import { api, createApiQuery } from "@/lib/api";
import { type components, queryKeys } from "@stockpilot/shared";
import { useI18n } from "@/providers/i18n-provider";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useActiveOrg } from "@/features/organizations/hooks/use-active-org";

export function OrganizationList() {
  const { t } = useI18n();
  const { db } = usePowerSyncContext();
  const ctx = useActiveOrg();
  const orgs = createApiQuery<
    components["schemas"]["OrganizationDTO"][] | null,
    components["schemas"]["ErrorModel"]
  >(
    () => queryKeys.auth.organizations(),
    async () => api.GET("/api/org/listOrganizations", {}),
  );

  const [adding, setAdding] = createSignal(false);
  const [name, setName] = createSignal("");
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  const canSubmit = () => Boolean(db()) && name().trim().length > 0 && !busy();

  const resetForm = () => {
    setAdding(false);
    setName("");
    setError(null);
  };

  // Alta de organización (empresa/supermercado): reutiliza `bootstrapOrg` (alta
  // atómica server-side + espera de sync + activación como org activa). Error
  // inline vía `handleApiResponse` (CC-30): nunca dejar el botón pegado sin
  // feedback (p. ej. un visitante recibe 401 y lo verá aquí en lugar de colgarse).
  async function createOrg(e: Event) {
    e.preventDefault();
    if (!canSubmit()) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await ctx.bootstrapOrg(name());
      resetForm();
      // La lista por API no es local-first; la refrescamos tras crear (el
      // switcher, que lee de PowerSync local, ya se actualizó solo).
      void orgs.refetch();
    } catch (err) {
      console.error("[OrganizationList] create organization failed:", err);
      setError(t(msg`We couldn't create your organization. Check your connection and try again.`));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div class="space-y-6">
      <div class="flex items-center justify-between">
        <h2 class="text-2xl font-bold tracking-tight">{t(msg`Organizations`)}</h2>
        <Button
          size="sm"
          class="gap-2"
          data-testid="org-list-new"
          onClick={() => setAdding((v) => !v)}
        >
          <Plus class="h-4 w-4" />
          {t(msg`New`)}
        </Button>
      </div>

      <Show when={adding()}>
        <Card>
          <CardContent class="p-4">
            <form class="space-y-3" onSubmit={createOrg}>
              <TextField
                data-testid="org-list-name-input"
                placeholder={t(msg`Company name`)}
                value={name()}
                onInput={(e) => setName(e.currentTarget.value)}
                autofocus
              />
              <div class="flex gap-2">
                <Button
                  data-testid="org-list-create"
                  type="submit"
                  size="sm"
                  disabled={!canSubmit()}
                >
                  {busy() ? t(msg`Creating…`) : t(msg`Create organization`)}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={resetForm}
                  disabled={busy()}
                >
                  {t(msg`Cancel`)}
                </Button>
              </div>
              <Show when={error()}>
                {(message) => (
                  <p role="alert" data-testid="org-list-error" class="text-sm text-destructive">
                    {message()}
                  </p>
                )}
              </Show>
            </form>
          </CardContent>
        </Card>
      </Show>

      <Show
        when={!orgs.isLoading}
        fallback={
          <div class="space-y-4">
            <For each={Array.from({ length: 3 })}>
              {() => <Skeleton class="h-20 w-full rounded-xl" />}
            </For>
          </div>
        }
      >
        <Show
          when={orgs.data && orgs.data.length > 0}
          fallback={
            <div class="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-border p-12 text-center">
              <Users class="mb-4 h-12 w-12 text-muted-foreground opacity-20" />
              <h3 class="text-lg font-semibold">{t(msg`No organizations`)}</h3>
              <p class="text-sm text-muted-foreground">
                {t(msg`Join one or create your own to collaborate.`)}
              </p>
            </div>
          }
        >
          <div class="grid gap-4">
            <For each={orgs.data}>
              {(org) => (
                <Link to="/organizations/$orgId" params={{ orgId: org.id }} class="group block">
                  <Card class="transition-[border-color,box-shadow] duration-200 ease-out hover:border-primary/50 hover:shadow-md">
                    <CardContent class="flex items-center gap-4 p-4">
                      <div class="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                        <Users class="h-6 w-6" />
                      </div>
                      <div class="flex-1 min-w-0">
                        <div class="flex items-center gap-2">
                          <h3 class="font-bold truncate">{org.name}</h3>
                          <Badge variant="outline" class="text-[10px]">
                            @{org.slug}
                          </Badge>
                        </div>
                        <p class="text-xs text-muted-foreground">
                          {(() => {
                            const id = org.id.slice(0, 8);
                            return t(msg`ID: ${id}...`);
                          })()}
                        </p>
                      </div>
                      <ChevronRight class="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                    </CardContent>
                  </Card>
                </Link>
              )}
            </For>
          </div>
        </Show>
      </Show>
    </div>
  );
}
